import { initNewRelicBrowser } from '../libs/newrelic';
import type { NewRelicBrowser, NewRelicErrorHandler } from '../libs/newrelic';
import type { App, ComponentPublicInstance } from 'vue';

export default defineNuxtPlugin(
  async ({
    vueApp,
    $router,
    $config: {
      public: {
        newRelicLicenseKey,
        newRelicApplicationId,
        serverEnv,
        newRelicEnabled,
        newRelicReleaseVersion,
        newRelicReleaseName,
      },
    },
  }) => {
    const newrelic = await initNewRelicBrowser({
      licenseKey: newRelicLicenseKey,
      applicationId: newRelicApplicationId,
      enabled: newRelicEnabled,
      environment: serverEnv,
      ajaxDenyList: [
        // local
        'localhost',
        '127.0.0.1',
        // Facebook flakiness
        'graph.facebook.com',
        // Facebook blocked
        'connect.facebook.net/en_US/all.js',
      ],
    });

    if (newrelic) {
      newrelic.setErrorHandler(createErrorHandler());
    }

    // Setup Vue error handler
    if (newrelic && vueApp) {
      setupVueErrorHandler(newrelic, vueApp);
    }

    if (newrelic) {
      if (newRelicReleaseVersion) {
        newrelic.setApplicationVersion(newRelicReleaseVersion);

        // NOTE: ソースマップアップロード時に指定したreleaseName, releaseVersionと合わせる必要がある
        // ref: https://ceblog.mediba.jp/post/664462603059445760/newrelic-%E3%83%96%E3%83%A9%E3%82%A6%E3%82%B6%E3%83%A2%E3%83%8B%E3%82%BF%E3%83%AA%E3%83%B3%E3%82%B0%E3%81%AE%E3%82%BD%E3%83%BC%E3%82%B9%E3%83%9E%E3%83%83%E3%83%97%E3%81%A7%E8%89%B2%E3%80%85%E3%83%8F%E3%83%9E%E3%81%A3%E3%81%9F%E8%A9%B1
        newrelic.addRelease(newRelicReleaseName, newRelicReleaseVersion);
      }
    }

    // Setup router tracking
    if (newrelic && $router) {
      const router = $router as {
        afterEach: (hook: (to: unknown, from: unknown) => void) => void;
      };
      router.afterEach((to: unknown) => {
        const route = to as { name?: string; path?: string };
        const routeName = route.name || route.path || 'Unknown';
        newrelic.setPageViewName(String(routeName));
      });
    }

    return {
      provide: {
        newrelic,
      },
    };
  }
);

function setupVueErrorHandler(agent: NewRelicBrowser, app: App): void {
  app.config.errorHandler = (
    error: unknown,
    instance: ComponentPublicInstance | null,
    info: string
  ) => {
    const attributes = {
      component: instance?.$options?.name || 'Unknown',
      errorInfo: info,
    };
    agent.noticeError(error, attributes);
  };
}

const ERROR_STACK_IGNORE_SUBSTRINGS = [
  // clarity
  "Cannot read properties of null (reading 'disconnect')",
  'n.replace is not a function',
  'clarity',
  // hooper(carousel library)
  "undefined is not an object (evaluating 't.touches[0]')",
  // bigbrother投げてる途中でページ遷移すると起こる
  'XMLHttpRequestProgressEvent',
  // 401
  'Unauthorized',
  'Not Authenticated',
  'Received status code 401',
  'Request failed with status code 401',
  'Response not successful: Received status code 401',
  // 重複回答
  'Already Exists',
  // Timeout
  'timeout',
  'Request timed out',
  // Network error
  'Network Error',
  'Failed to fetch',
  'Load failed',
  'Request aborted',
  'socket is not opened',
  // 謎の拡張エラー
  'learningToolsRuntimeBridge',
  // 404
  'アンケートが見つかりません',
  // Chrome extensions
  'extensions/',
  'chrome://',
  'chrome-extension://',
  // local file
  'file://',
  'googletagmanager.com',
  // ブラウザ仕様上の警告（無害）
  'ResizeObserver loop',
] as const;

function createErrorHandler(): NewRelicErrorHandler {
  return (error) => {
    if (!error) {
      return false;
    }

    const stack = extractStack(error);
    if (!stack) {
      return false;
    }

    const normalizedStack = stack.toLowerCase();

    const shouldIgnore = ERROR_STACK_IGNORE_SUBSTRINGS.some((substring) =>
      normalizedStack.includes(substring.toLowerCase())
    );

    return shouldIgnore;
  };
}

function extractStack(error: unknown): string | null {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.stack || null;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'stack' in error &&
    typeof (error as { stack?: unknown }).stack === 'string'
  ) {
    return (error as { stack: string }).stack;
  }

  return null;
}
